/**
 * PricingPageB - Full Page Variant (v2)
 * 
 * Dedicated pricing page with hero, pricing cards, FAQ, and back link.
 * Good for SEO and shareable URLs.
 * 
 * v2 Changes:
 * - 2 pricing cards (Monthly + Lifetime) instead of 3
 * - Updated comparison table (removed yearly)
 * - Trust signals section
 * - Outcome-focused copy
 */

import { useState } from 'react';
import { Link } from 'react-router-dom';
import { BenefitsList } from './shared/BenefitsList';
import { PricingCard } from './shared/PricingCard';
import { PRICING, FAQ_ITEMS, PricingPlan, TRUST_SIGNALS } from './shared/constants';
import { useLanguage } from '@/i18n';
import './PricingPageB.css';

interface PricingPageBProps {
  onSelectPlan?: (plan: PricingPlan) => void;
}

export function PricingPageB({ onSelectPlan }: PricingPageBProps) {
  const { t } = useLanguage();
  const [expandedFaq, setExpandedFaq] = useState<number | null>(null);

  const handleSelectPlan = (plan: PricingPlan) => {
    console.log('[PricingPage] Plan selected:', plan);
    if (onSelectPlan) {
      onSelectPlan(plan);
    } else {
      // Default behavior - could redirect to checkout
      alert(`Selected plan: ${PRICING[plan].label}. Checkout integration pending.`);
    }
  };

  const toggleFaq = (index: number) => {
    setExpandedFaq(expandedFaq === index ? null : index);
  };

  return (
    <div className="pricing-page">
      {/* Background decoration */}
      <div className="pricing-page-bg" aria-hidden="true">
        <div className="pricing-page-glow pricing-page-glow--1" />
        <div className="pricing-page-glow pricing-page-glow--2" />
        <div className="pricing-page-grid" />
      </div>

      {/* Back link */}
      <div className="pricing-page-nav">
        <Link to="/settings" className="pricing-back-link">
          <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
            <line x1="19" y1="12" x2="5" y2="12" />
            <polyline points="12 19 5 12 12 5" />
          </svg>
          {t('funnel.back_to_app')}
        </Link>
      </div>

      {/* Hero section */}
      <header className="pricing-hero">
        <div className="pricing-hero-badge">
          <span className="hero-badge-icon">
            <svg width="16" height="16" viewBox="0 0 24 24" fill="currentColor">
              <path d="M12 2L15.09 8.26L22 9.27L17 14.14L18.18 21.02L12 17.77L5.82 21.02L7 14.14L2 9.27L8.91 8.26L12 2Z" />
            </svg>
          </span>
          NinjaCash Pro
        </div>
        
        <h1 className="pricing-hero-title">
          {t('funnel.hero_title')}
          <span className="pricing-hero-title-accent">{t('funnel.hero_accent')}</span>
        </h1>

        <p className="pricing-hero-subtitle">
          {t('funnel.hero_sub1')}
          <br />
          {t('funnel.modal_sub_default')}
        </p>
      </header>

      {/* v2: Two pricing cards */}
      <section className="pricing-cards-section">
        <div className="pricing-cards pricing-cards--v2">
          <PricingCard 
            plan="monthly" 
            highlighted
            onSelect={handleSelectPlan}
          />
          <PricingCard 
            plan="lifetime" 
            onSelect={handleSelectPlan}
          />
        </div>

        {/* v2: Trust signals */}
        <div className="pricing-trust-row">
          <span className="trust-signal">
            <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
              <path d="M12 22s8-4 8-10V5l-8-3-8 3v7c0 6 8 10 8 10z" />
            </svg>
            {t(TRUST_SIGNALS.securePayment)}
          </span>
          <span className="trust-signal">
            <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
              <polyline points="20 6 9 17 4 12" />
            </svg>
            {t(TRUST_SIGNALS.cancelAnytime)}
          </span>
        </div>
      </section>

      {/* Benefits section */}
      <section className="pricing-benefits-section">
        <h2 className="pricing-section-title">{t('funnel.everything')}</h2>
        <BenefitsList variant="detailed" />
      </section>

      {/* Comparison section - v2: removed yearly */}
      <section className="pricing-comparison-section">
        <h2 className="pricing-section-title">{t('funnel.free_vs_pro')}</h2>
        <div className="comparison-table">
          <div className="comparison-header">
            <div className="comparison-feature-label">{t('funnel.col_feature')}</div>
            <div className="comparison-plan-label">{t('funnel.col_free')}</div>
            <div className="comparison-plan-label comparison-plan-label--pro">{t('funnel.col_pro')}</div>
          </div>
          
          <div className="comparison-row">
            <div className="comparison-feature">{t('funnel.feat_track')}</div>
            <div className="comparison-check comparison-check--yes">
              <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="3"><polyline points="20 6 9 17 4 12" /></svg>
            </div>
            <div className="comparison-check comparison-check--yes">
              <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="3"><polyline points="20 6 9 17 4 12" /></svg>
            </div>
          </div>
          
          <div className="comparison-row">
            <div className="comparison-feature">{t('funnel.feat_accounts')}</div>
            <div className="comparison-check comparison-check--yes">
              <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="3"><polyline points="20 6 9 17 4 12" /></svg>
            </div>
            <div className="comparison-check comparison-check--yes">
              <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="3"><polyline points="20 6 9 17 4 12" /></svg>
            </div>
          </div>
          
          <div className="comparison-row">
            <div className="comparison-feature">{t('funnel.feat_analytics')}</div>
            <div className="comparison-check comparison-check--yes">
              <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="3"><polyline points="20 6 9 17 4 12" /></svg>
            </div>
            <div className="comparison-check comparison-check--yes">
              <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="3"><polyline points="20 6 9 17 4 12" /></svg>
            </div>
          </div>
          
          <div className="comparison-row comparison-row--highlight">
            <div className="comparison-feature">{t('funnel.feat_sync')}</div>
            <div className="comparison-check comparison-check--no">
              <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><line x1="18" y1="6" x2="6" y2="18" /><line x1="6" y1="6" x2="18" y2="18" /></svg>
            </div>
            <div className="comparison-check comparison-check--yes">
              <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="3"><polyline points="20 6 9 17 4 12" /></svg>
            </div>
          </div>
          
          <div className="comparison-row comparison-row--highlight">
            <div className="comparison-feature">{t('funnel.feat_multi')}</div>
            <div className="comparison-check comparison-check--no">
              <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><line x1="18" y1="6" x2="6" y2="18" /><line x1="6" y1="6" x2="18" y2="18" /></svg>
            </div>
            <div className="comparison-check comparison-check--yes">
              <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="3"><polyline points="20 6 9 17 4 12" /></svg>
            </div>
          </div>
          
          <div className="comparison-row comparison-row--highlight">
            <div className="comparison-feature">{t('funnel.feat_backup')}</div>
            <div className="comparison-check comparison-check--no">
              <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><line x1="18" y1="6" x2="6" y2="18" /><line x1="6" y1="6" x2="18" y2="18" /></svg>
            </div>
            <div className="comparison-check comparison-check--yes">
              <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="3"><polyline points="20 6 9 17 4 12" /></svg>
            </div>
          </div>
        </div>
      </section>

      {/* FAQ section */}
      <section className="pricing-faq-section">
        <h2 className="pricing-section-title">{t('funnel.faq_title')}</h2>
        <div className="faq-list">
          {FAQ_ITEMS.map((item, index) => (
            <div 
              key={index} 
              className={`faq-item ${expandedFaq === index ? 'faq-item--expanded' : ''}`}
            >
              <button 
                className="faq-question"
                onClick={() => toggleFaq(index)}
                aria-expanded={expandedFaq === index}
              >
                <span>{t(item.question)}</span>
                <svg 
                  className="faq-chevron" 
                  width="20" 
                  height="20" 
                  viewBox="0 0 24 24" 
                  fill="none" 
                  stroke="currentColor" 
                  strokeWidth="2" 
                  strokeLinecap="round" 
                  strokeLinejoin="round"
                >
                  <polyline points="6 9 12 15 18 9" />
                </svg>
              </button>
              <div className="faq-answer">
                <p>{t(item.answer)}</p>
              </div>
            </div>
          ))}
        </div>
      </section>

      {/* Final CTA - v2: leads with trial */}
      <section className="pricing-final-cta">
        <h2 className="final-cta-title">{t('funnel.final_cta_title')}</h2>
        <p className="final-cta-subtitle">{t('funnel.final_cta_sub')}</p>
        <button
          className="final-cta-button"
          onClick={() => handleSelectPlan('monthly')}
        >
          {t('funnel.monthly_cta')}
        </button>
        <p className="final-cta-note">{t('funnel.final_cta_note')}</p>
      </section>

      {/* Footer */}
      <footer className="pricing-footer">
        <Link to="/privacy" className="pricing-footer-link">{t('settings.legal.privacy')}</Link>
        <span className="pricing-footer-divider">|</span>
        <Link to="/terms" className="pricing-footer-link">{t('settings.legal.terms')}</Link>
      </footer>
    </div>
  );
}
